import { action, makeObservable, observable } from 'mobx';

import { Stack } from 'src/packages/data/structures/Stack';
import { requireService } from 'src/packages/di';
import type { BaseSettingsScreen } from 'src/pages/settings/features/screen/BaseSettingsScreen';

import { AlertsEntity } from '../alerts/Alerts.entity';
import { BasicParametersEntity } from '../basic-parameters/BasicParameters.entity';
import { DevSettingsScreenEntity } from '../dev-settings/DevSettingsScreen.entity';
import { ObjectListScreenEntity } from '../objects-list-screen/ObjectsListScreen.entity';
import { OthersEntity } from '../others/Others.entity';
import { TabTemplatesListScreenEntity } from '../tab-templates-list-screen/TabTemplatesListScreen.entity';

import type { TSettingType } from './types';

export class SettingsStore {
  readonly stack: Stack<BaseSettingsScreen>;

  @observable mainColumn?: string;

  constructor(
    defaultId?: string,
    private readonly wellLogsTemplateService = requireService('wellLogsTemplateService'),
  ) {
    this.stack = new Stack<BaseSettingsScreen>();

    if (defaultId) {
      this.createObjectList(defaultId);
    }

    makeObservable(this);
  }

  createSettingsScreen = (
    key: TSettingType,
    withConfig?: boolean,
  ): ((id: string, config?: { wellId?: number; wellboreId?: number }) => void) => {
    this.stack.setLength(0);

    switch (key) {
      case 'widgetsManager':
        return this.createObjectList;
      case 'tabsManager':
        return this.createTabTemplateList;
      case 'devSettings':
        return this.createDevPage;
      case 'basicParameters':
        if (withConfig) {
          return this.createBasicParametersWithConfig;
        }
        return this.createBasicParameters;
      // case 'alerts':
      //   return this.createAlerts;
      case 'others':
        return this.createOthers;
      default:
        return this.setMainColumn;
    }
  };

  @action.bound
  setMainColumn = (mainColumn: string): void => {
    this.mainColumn = mainColumn;
  };

  @action.bound
  createObjectList = (id: string) => {
    if (this.mainColumn === id) return;

    this.stack.clearStack();
    this.setMainColumn(id);
    this.stack.push(new ObjectListScreenEntity(this.stack));
  };

  @action.bound
  createTabTemplateList = (id: string) => {
    if (this.mainColumn === id) return;

    this.stack.clearStack();
    this.setMainColumn(id);
    this.stack.push(new TabTemplatesListScreenEntity(this.stack));
  };

  @action.bound
  createDevPage = (id: string) => {
    if (this.mainColumn === id) return;

    this.stack.clearStack();
    this.setMainColumn(id);
    this.stack.push(new DevSettingsScreenEntity(this.stack));
  };

  @action.bound
  createBasicParameters = (id: string) => {
    if (this.mainColumn === id) return;

    this.stack.clearStack();
    this.setMainColumn(id);
    this.stack.push(new BasicParametersEntity(this.stack));
  };

  @action.bound
  createBasicParametersWithConfig = (id: string, config?: { wellId?: number; wellboreId?: number }) => {
    this.stack.clearStack();
    this.setMainColumn(id);
    const entity = new BasicParametersEntity(this.stack);
    entity.setConfig(config);
    this.stack.push(entity);
  };

  @action.bound
  createAlerts = (id: string) => {
    if (this.mainColumn === id) return;

    this.stack.clearStack();
    this.setMainColumn(id);
    this.stack.push(new AlertsEntity(this.stack));
  };

  @action.bound
  createOthers = (id: string) => {
    if (this.mainColumn === id) return;

    this.stack.clearStack();
    this.setMainColumn(id);
    this.stack.push(new OthersEntity(this.stack));
  };

  effect = () => {
    return () => this.wellLogsTemplateService.destroy();
  };
}
